import React from "react";
import { BrowserRouter, Routes, Route, NavLink } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useTranslation, Trans } from "react-i18next";
import { WalletConnectProvider, useWalletConnect } from "@btc-vision/walletconnect";

import { DashboardPage }             from "./pages/Dashboard";
import { CreateEscrowPage }          from "./pages/CreateEscrow";
import { EscrowDetailPage }          from "./pages/EscrowDetail";
import { EscrowListPage }            from "./pages/EscrowList";
import { ArbitratorMarketplacePage } from "./pages/ArbitratorMarketplace";
import { UserReputationPage }        from "./pages/UserReputation";
import { AdminSetupPage }            from "./pages/AdminSetup";
import { DocsPage }                  from "./pages/Docs";
import { LanguageSelector }          from "./components/LanguageSelector";
import { NetworkSelector }           from "./components/NetworkSelector";
import "./styles/global.css";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 2, staleTime: 15_000 } },
});

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const { t } = useTranslation();

  return (
    <WalletConnectProvider theme="dark">
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <div className="app">
          <nav className="navbar">
            <NavLink to="/" className="nav-brand">
              <span className="logo">⚡</span>
              MotoSettle
            </NavLink>

            <div className="nav-links">
              <NavLink to="/" end>{t("nav.dashboard")}</NavLink>
              <NavLink to="/create">{t("nav.create")}</NavLink>
              <NavLink to="/escrows">Escrows</NavLink>
              <NavLink to="/arbitrators">{t("nav.arbitrators")}</NavLink>
              <NavLink to="/reputation">{t("nav.reputation")}</NavLink>
              <NavLink to="/docs">Docs</NavLink>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <NetworkSelector />
              <LanguageSelector />
              <WalletButton />
            </div>
          </nav>

          <main className="main">
            <Routes>
              <Route path="/"              element={<DashboardPage />} />
              <Route path="/create"        element={<CreateEscrowPage />} />
              <Route path="/escrows"       element={<EscrowListPage />} />
              <Route path="/escrow/:id"    element={<EscrowDetailPage />} />
              <Route path="/arbitrators"   element={<ArbitratorMarketplacePage />} />
              <Route path="/reputation"    element={<UserReputationPage />} />
              <Route path="/admin"         element={<AdminSetupPage />} />
              <Route path="/docs"          element={<DocsPage />} />
            </Routes>
          </main>

          <footer className="footer">
            <span>
              <Trans i18nKey="footer.text">
                MotoSettle <strong>v1.0.0</strong> — Trustless Escrow on OP_NET
              </Trans>
            </span>
            <a href="https://gitlab.com/acceleralabs/motosettle" target="_blank" rel="noreferrer">
              {t("footer.gitlab")}
            </a>
          </footer>
        </div>
      </BrowserRouter>
    </QueryClientProvider>
    </WalletConnectProvider>
  );
}

// ─── Wallet Button ─────────────────────────────────────────────────────────────
function WalletButton() {
  const { walletAddress, connecting, openConnectModal, disconnect } = useWalletConnect();
  const { t } = useTranslation();

  if (walletAddress) {
    return (
      <div className="wallet-connected" style={{ cursor: "pointer" }} onClick={disconnect}>
        {t("wallet.connected")}: {walletAddress.slice(0, 6)}…{walletAddress.slice(-4)}
      </div>
    );
  }

  return (
    <button
      className="wallet-btn"
      onClick={openConnectModal}
      disabled={connecting}
    >
      {connecting ? t("wallet.connecting") : t("wallet.connect")}
    </button>
  );
}
